import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Gift, Plus, Edit, Trash2, Search, X, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import S3ImageUpload from '../../components/S3ImageUpload';
import { toast } from 'sonner';

import API from '../../config/api';

const AdminBhets = () => {
  const [bhets, setBhets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBhet, setEditingBhet] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    image_url: '',
    is_active: true
  });

  useEffect(() => {
    fetchBhets();
  }, []);

  const fetchBhets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bhets/all`, { withCredentials: true });
      setBhets(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading bhets:', error);
      toast.error('Failed to load bhets');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingBhet(null);
    setFormData({
      name: '',
      price: '',
      description: '',
      image_url: '',
      is_active: true
    });
    setShowModal(true);
  };

  const handleOpenEdit = (bhet) => {
    setEditingBhet(bhet);
    setFormData({
      name: bhet.name || '',
      price: bhet.price !== undefined ? bhet.price : '',
      description: bhet.description || '',
      image_url: bhet.image_url || '',
      is_active: bhet.is_active !== undefined ? bhet.is_active : true
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Bhet name is required');
      return;
    }
    if (uploadingImage || submitting) return;
    if (String(formData.price).trim() === '' || !Number.isFinite(Number(formData.price)) || Number(formData.price) < 0) {
      toast.error('Valid price is required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        name: formData.name.trim(),
        description: formData.description.trim(),
        price: Number(formData.price)
      };

      if (editingBhet) {
        await axios.put(`${API}/bhets/${editingBhet.id}`, payload, { withCredentials: true });
        toast.success('Bhet updated successfully');
      } else {
        await axios.post(`${API}/bhets`, payload, { withCredentials: true });
        toast.success('Bhet added successfully');
      }
      setShowModal(false);
      fetchBhets();
    } catch (error) {
      console.error('Error saving bhet:', error);
      toast.error(error.response?.data?.detail || 'Failed to save bhet');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      await axios.delete(`${API}/bhets/${id}`, { withCredentials: true });
      toast.success('Bhet deleted');
      fetchBhets();
    } catch (error) {
      console.error('Error deleting bhet:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete bhet');
    }
  };

  const handleToggleActive = async (bhet) => {
    setUpdatingId(bhet.id);
    try {
      await axios.put(
        `${API}/bhets/${bhet.id}`,
        { is_active: !bhet.is_active },
        { withCredentials: true }
      );
      toast.success(`Bhet ${!bhet.is_active ? 'shown' : 'hidden'}`);
      fetchBhets();
    } catch (error) {
      console.error('Error toggling bhet status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredBhets = bhets.filter(b => 
    b.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-200/80 shadow-xs">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900 flex items-center gap-2.5">
            <Gift className="w-6 h-6 text-[#FF7B00]" />
            Bhet &amp; Daan Offerings
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage sacred offerings (Vastra Daan, Gau Seva, Brahman Bhojan, etc.) available for devotees to add inside Pujas.
          </p>
        </div>
        <Button 
          onClick={handleOpenAdd}
          className="btn-saffron flex items-center gap-2 px-5 py-2.5 self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          Add New Bhet
        </Button>
      </div>

      {/* Filter / Search Bar */}
      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl border border-gray-200/80 shadow-xs">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title or description..."
            className="pl-9 bg-gray-50/50 border-gray-200"
          />
        </div>
        <div className="text-xs text-gray-500 whitespace-nowrap">
          Total: <span className="font-bold text-gray-800">{bhets.length}</span> offerings
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">
            <div className="w-8 h-8 border-4 border-[#FF7B00] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            Loading Bhets...
          </div>
        ) : filteredBhets.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Gift className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="font-medium text-gray-600">No Bhet offerings found</p>
            <p className="text-xs text-gray-400 mt-1">Click "Add New Bhet" to create your first offering.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-[#FAF8F5] text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                <tr>
                  <th className="py-3.5 px-4">Offering</th>
                  <th className="py-3.5 px-4">Price</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredBhets.map((bhet) => (
                  <tr key={bhet.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gray-100 border border-gray-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
                          {bhet.image_url ? (
                            <img src={bhet.image_url} alt={bhet.name} className="w-full h-full object-cover" />
                          ) : (
                            <Gift className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">{bhet.name}</p>
                          <p className="text-xs text-gray-500 line-clamp-1 max-w-xs">{bhet.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-bold text-[#FF7B00]">₹{bhet.price}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => handleToggleActive(bhet)}
                        disabled={updatingId === bhet.id}
                        aria-label={`${bhet.is_active ? 'Hide' : 'Show'} ${bhet.name}`}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                          bhet.is_active 
                            ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' 
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {bhet.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {bhet.is_active ? 'Shown' : 'Hidden'}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEdit(bhet)}
                          aria-label={`Edit ${bhet.name}`}
                          className="h-8 w-8 text-gray-600 hover:text-[#FF7B00] hover:bg-orange-50 rounded-lg"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(bhet.id, bhet.name)}
                          aria-label={`Delete ${bhet.name}`}
                          className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="font-heading text-lg font-bold text-gray-900">
                {editingBhet ? 'Edit Bhet Offering' : 'Add New Bhet Offering'}
              </h3>
              <button 
                disabled={submitting || uploadingImage}
                onClick={() => setShowModal(false)}
                aria-label="Close"
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="bhet-title" className="text-xs font-semibold text-gray-700">Title *</Label>
                <Input
                  required
                  id="bhet-title"
                  placeholder="e.g. Vastra Daan & Chola Offering"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="bhet-price" className="text-xs font-semibold text-gray-700">Price (₹) *</Label>
                <Input id="bhet-price" required type="number" min="0" step="0.01"
                  placeholder="e.g. 501" value={formData.price}
                  onChange={e => setFormData({ ...formData, price: e.target.value })} className="mt-1" />
              </div>

              <div>
                <Label htmlFor="bhet-description" className="text-xs font-semibold text-gray-700">Description</Label>
                <textarea
                  id="bhet-description"
                  rows="3"
                  placeholder="Brief description of sacred significance..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full mt-1 p-2.5 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF7B00]/20 focus:border-[#FF7B00]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-700">Image</Label>
                <S3ImageUpload value={formData.image_url}
                  onChange={image_url => setFormData(prev => ({ ...prev, image_url }))}
                  onUploadingChange={setUploadingImage} disabled={submitting} />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="bhet-active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 accent-orange-500 focus:ring-[#FF7B00]"
                />
                <label htmlFor="bhet-active" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Show on website
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  disabled={submitting || uploadingImage}
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting || uploadingImage}
                  className="btn-saffron"
                >
                  {submitting ? 'Saving...' : editingBhet ? 'Update Bhet' : 'Create Bhet'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBhets;
